import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Ruler, Weight, CircleDot } from "lucide-react";
import { site } from "../content";
import { WaCta } from "./WaCta";
import { Reveal, SectionHeading } from "./Reveal";

const galleries = {
  mesa: {
    images: site.assets.modelMesa,
    alts: [
      "Barber Steamer versão mesa — visão frontal",
      "Interior em aço inoxidável 304 do Barber Steamer",
    ],
  },
  tripe: {
    images: site.assets.modelTripe,
    alts: [
      "Barber Steamer versão com tripé — visão frontal",
      "Barber Steamer versão com tripé — visão traseira com instruções de uso",
      "Barber Steamer com tripé ao lado do fundador Roberto Nascimento",
    ],
  },
};

function ModelCard({ model, delay }) {
  const [active, setActive] = useState(0);
  const gallery = galleries[model.id];
  return (
    <Reveal delay={delay}>
      <article
        data-testid={`model-card-${model.id}`}
        className="group h-full overflow-hidden rounded-3xl border border-white/10 bg-graphite/60 backdrop-blur-xl transition-[border-color,box-shadow] duration-500 hover:border-gold/40 hover:shadow-[0_24px_70px_rgba(252,172,4,0.1)]"
      >
        <div className="relative h-80 overflow-hidden bg-[#0d0d0d]">
          <AnimatePresence mode="wait">
            <motion.img
              key={active}
              src={gallery.images[active]}
              alt={gallery.alts[active]}
              loading="lazy"
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.45, ease: "easeOut" }}
              className="h-full w-full object-contain"
            />
          </AnimatePresence>
          <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" aria-hidden="true" />
          <span className="absolute left-6 top-6 rounded-full border border-gold/40 bg-ink/70 px-4 py-1.5 font-display text-[11px] font-bold uppercase tracking-[0.25em] text-gold backdrop-blur-md">
            {model.id === "mesa" ? "Versão Mesa" : "Versão Tripé"}
          </span>
        </div>

        <div className="flex gap-2.5 px-8 pt-5 md:px-10">
          {gallery.images.map((img, idx) => (
            <button
              key={img}
              data-testid={`model-${model.id}-thumb-${idx}`}
              onClick={() => setActive(idx)}
              aria-label={`Ver foto ${idx + 1} do ${model.name}`}
              className={`h-14 w-14 overflow-hidden rounded-lg border transition-[border-color,opacity] duration-300 ${
                active === idx ? "border-gold" : "border-white/10 opacity-60 hover:opacity-100"
              }`}
            >
              <img src={img} alt="" loading="lazy" className="h-full w-full object-contain" />
            </button>
          ))}
        </div>

        <div className="p-8 md:px-10 md:pb-10">
          <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight text-bone">
            {model.name}
          </h3>

          <ul className="mt-7 space-y-3.5 text-sm text-bone/70">
            <li className="flex items-center gap-3">
              <Ruler className="h-4 w-4 text-gold" aria-hidden="true" />
              {model.height}
            </li>
            <li className="flex items-center gap-3">
              <CircleDot className="h-4 w-4 text-gold" aria-hidden="true" />
              {model.diameter}
            </li>
            <li className="flex items-center gap-3">
              <Weight className="h-4 w-4 text-gold" aria-hidden="true" />
              {model.weight}
            </li>
          </ul>

          <p className="mt-6 border-t border-white/10 pt-6 text-sm leading-relaxed text-bone/55">
            {model.note}
          </p>

          <WaCta
            message={site.messages[model.messageKey]}
            location={model.location}
            label={model.cta}
            variant="outline"
            testId={`model-${model.id}-cta`}
            className="mt-8 w-full"
          />
        </div>
      </article>
    </Reveal>
  );
}

export default function Models() {
  return (
    <section id="modelos" data-testid="models-section" className="relative py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <SectionHeading
          chapter="07"
          eyebrow="Modelos"
          title="Escolha a configuração ideal para sua barbearia"
          testId="models-heading"
        />

        <div className="mt-16 grid gap-8 lg:grid-cols-2">
          {site.models.map((model, i) => (
            <ModelCard key={model.id} model={model} delay={i * 0.12} />
          ))}
        </div>
      </div>
    </section>
  );
}
